import { PlanPriceInterval } from '@/graphql/generated/types.generated';
import type { MappedFeature, MappedPlan } from './map-graphql-to-calculator.util';
import { calcTieredCost } from './tier-pricing.util';
import { round } from '@/utils/round';
import { PLANS_YEARLY_DISCOUNT } from '@/constants';

export interface AddOnLine {
    feature: MappedFeature;
    extraQty: number;
    boolEnabled: boolean;
    enumValue: string;
}

/**
 * Sum of all active add-on prices at raw monthly rates.
 * No interval discount is applied here — discount is applied once at the total level.
 */
export function calcRawAddOns(addOnLines: AddOnLine[]): number {
    return addOnLines.reduce((sum, line) => {
        if (line.extraQty > 0) {
            if (line.feature.addOnTiers?.length) {
                return sum + calcTieredCost(line.extraQty, line.feature.addOnTiers, line.feature.value);
            }
            if (line.feature.addOnPricePerUnit) {
                return sum + line.extraQty * line.feature.addOnPricePerUnit;
            }
        }
        if (line.feature.addOnPrice && line.boolEnabled) {
            return sum + line.feature.addOnPrice;
        }
        if (line.feature.addOnOptions && line.enumValue && line.enumValue !== line.feature.displayValue) {
            const sel = line.feature.addOnOptions.find((o) => o.value === line.enumValue);
            if (sel) return sum + sel.price;
        }
        return sum;
    }, 0);
}

/**
 * Monthly price shown in the summary.
 * - Base: gateway's yearlyMonthlyPrice (already discounted by Stripe) or raw monthlyPrice.
 * - Add-ons: raw monthly sum with yearly discount applied once as a basket-level reduction.
 */
export function calcTotalMonthlyPrice(
    plan: MappedPlan,
    addOnLines: AddOnLine[],
    interval: PlanPriceInterval,
): number {
    const basePlanMonthly = interval === PlanPriceInterval.Year ? plan.yearlyMonthlyPrice : plan.monthlyPrice;
    const rawAddOns = calcRawAddOns(addOnLines);
    const discountedAddOns =
        interval === PlanPriceInterval.Year ? round(rawAddOns * PLANS_YEARLY_DISCOUNT, 2) : rawAddOns;
    return basePlanMonthly + discountedAddOns;
}

/**
 * Total amount billed annually.
 * Computed from the raw monthly basis × 12 × discount to avoid compounding
 * gateway rounding from yearlyMonthlyPrice.
 */
export function calcTotalYearlyPrice(
    plan: MappedPlan,
    addOnLines: AddOnLine[],
    interval: PlanPriceInterval,
): number {
    const rawMonthlyTotal = plan.monthlyPrice + calcRawAddOns(addOnLines);
    if (interval === PlanPriceInterval.Year) {
        return round(rawMonthlyTotal * 12 * PLANS_YEARLY_DISCOUNT, 2);
    }
    return round(rawMonthlyTotal * 12, 2);
}

/**
 * Actual amount to pay for the billing cycle — what is sent to the backend.
 * - Monthly interval: raw monthly total (plan + add-ons, no discount)
 * - Yearly interval: (plan.monthlyPrice + add-ons) × 12 × PLANS_YEARLY_DISCOUNT
 *
 * Uses plan.monthlyPrice (always undiscounted) as the base so the yearly
 * formula is not double-discounted via yearlyMonthlyPrice.
 */
export function calcTotalToPay(
    plan: MappedPlan,
    addOnLines: AddOnLine[],
    interval: PlanPriceInterval,
): number {
    const rawMonthlyTotal = plan.monthlyPrice + calcRawAddOns(addOnLines);
    if (interval === PlanPriceInterval.Year) {
        return round(rawMonthlyTotal * 12 * PLANS_YEARLY_DISCOUNT, 2);
    }
    return round(rawMonthlyTotal, 2);
}
